import { ENV_VARS } from 'constants/environment.constants';
import { HttpStatusCodeResponse } from 'constants/http-statuscodes';
import { kf1AuthenticatedHttpClient } from 'kfone-frontend-core';

// needs to be updated based on environment and api endpoint
const apiURL = ENV_VARS.API_URL;

export interface ICompensation {
  id: number;
  compensationName: string;
  compensationDescription: string;
}

export interface ICompensationElementItem {
  compensationId: string;
  compensationName: string;
  compensationDescription: string;
  isFlag: boolean;
  includeZeroVariablePay: boolean | null;
}

export interface IVariablePayItem {
  compensationId: string;
  compensationName: string;
  compensationDescription: string;
  isFlag: boolean;
  includeZeroVariablePay: boolean | null;
}

export interface ICompensationApiData {
  compensationElement: ICompensationElementItem[];
  variablePay: IVariablePayItem[];
}

export interface ICompensationResponse {
  reportId?: number;
  compensation?: ICompensation[] | [];
  data?: ICompensationApiData;
  responseCode?: number;
  responseMessage?: string;
}

export interface IDemographic {
  demographicId: number;
  demographicName: string;
  isFlag: boolean;
}

export interface IDemographicResponse {
  reportId: number;
  demographic: IDemographic[] | [];
}

export const getCompensation = async (
  reportId = 1001,
): Promise<{ responseCode: number; responseMessage: string; data: ICompensationApiData }> => {
  try {
    const config = {
      url: 'v3/elements/compensation' + `?reportId=${reportId}`,
      method: 'GET',
      baseURL: apiURL,
    };
    const res = await kf1AuthenticatedHttpClient(config);

    return res.data;
  } catch (error) {
    console.error('Error fetching compensation:', error);
    throw error;
  }
};

export const getDemographic = async (reportId = 1001): Promise<IDemographicResponse> => {
  try {
    const config = {
      url: 'v2/elements/demographic' + `?reportId=${reportId}`,
      method: 'GET',
      baseURL: apiURL,
    };
    const res = await kf1AuthenticatedHttpClient(config);

    return res.data;
  } catch (error) {
    console.error('Error fetching demographics:', error);
    throw error;
  }
};

export const getUserCustomGroupsData = async (reportId = 1001): Promise<any> => {
  try {
    const config = {
      url: `v2/elements/group?reportId=${reportId}`,
      method: 'GET',
      baseURL: apiURL,
    };
    const res = await kf1AuthenticatedHttpClient(config);

    return res.data;
  } catch (error) {
    console.error('Error fetching demographics:', error);
    throw error;
  }
};

export const getCustomGroupMetadata = async (reportId = 1001): Promise<any> => {
  try {
    const config = {
      url: `v2/elements/metadata?reportId=${reportId}`,
      method: 'GET',
      baseURL: apiURL,
    };
    const res = await kf1AuthenticatedHttpClient(config);

    return res.data;
  } catch (error) {
    console.error('Error fetching demographics:', error);
    throw error;
  }
};

export const postCompensationData = async (data: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'v2/elements/compensation',
      method: 'POST',
      data,
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching compensation data:', error);
    throw error;
  }
};

export const postDemographicData = async (data: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'elements/demographic',
      method: 'POST',
      data,
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching demographic data:', error);
    throw error;
  }
};

export const postCustomGroupData = async (data: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'elements/group',
      method: 'POST',
      data,
    });

    return response.data;
  } catch (error: any) {
    console.error('Error fetching groups data:', error);
    return error?.response?.data ?? HttpStatusCodeResponse.InternalServerError;
  }
};

export const removeCustomGroup = async (groupId: any, reportId: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: `elements/group?groupId=${groupId}&reportId=${reportId}`,
      method: 'DELETE',
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching demographic data:', error);
    throw error;
  }
};

export const createReport = async (data: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'v2/report/create',
      method: 'POST',
      data,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error creating report:', error?.response?.data);
    return error?.response?.data ?? HttpStatusCodeResponse.InternalServerError;
  }
};

export const getFactors = async (reportId = 1001): Promise<IDemographicResponse> => {
  try {
    const config = {
      url: 'v2/dataload/factor' + `?reportId=${reportId}`,
      method: 'GET',
      baseURL: apiURL,
    };
    const res = await kf1AuthenticatedHttpClient(config);
    return res.data;
  } catch (error) {
    console.error('Error fetching factors:', error);
    throw error;
  }
};

export const postFactors = async (data: any): Promise<IDemographicResponse> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'dataload/factor',
      method: 'POST',
      data,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching demographic data:', error);
    throw error;
  }
};

export const clonePreviousReport = async (params: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'dataload/clonereport',
      method: 'POST',
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Error cloning previous report:', error);
    throw error;
  }
};

export const updateReportInternalStatus = async (params: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'report/updatereportstatus',
      method: 'POST',
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating report internal status:', error);
    throw error;
  }
};

export const updateConfigAndPopulationStatus = async (data: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'report/updateconfigpopulation',
      method: 'POST',
      data,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error updating config and population status:', error);
    return error?.response?.data ?? HttpStatusCodeResponse.InternalServerError;
  }
};

export const getReportHeadCount = async (params: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'overview/headcount',
      method: 'GET',
      params,
    });
    return response.data;
  }
  catch (error: any) {
    console.error('Error fetching report employee count:', error);
    return error?.response?.data ?? HttpStatusCodeResponse.InternalServerError;
  }
};

export const getExclusionList = async (reportId = 0): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'exclusion/rule?reportId=' + reportId,
      method: 'GET',
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching exclusion list:', error);
    return error?.response?.data ?? HttpStatusCodeResponse.InternalServerError;
  }
};

export const postExclusionList = async (data: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'exclusion/rule',
      method: 'POST',
      data,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error updating exclusion list:', error);
    return error?.response?.data ?? HttpStatusCodeResponse.InternalServerError;
  }
};

export const deleteExclusionList = async (params: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'exclusion/rule',
      method: 'DELETE',
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error deleting exclusion list:', error);
    return error?.response?.data ?? HttpStatusCodeResponse.InternalServerError;
  }
};

export const getExclusionEmployeeCount = async (data: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'exclusion/exclusionemployeescount',
      method: 'POST',
      data,
    });
    return response.data;
  }
  catch (error: any) {
    console.error('Error fetching exclusion employee count:', error);
    return error?.response?.data ?? HttpStatusCodeResponse.InternalServerError;
  }
};

export const getIncumbentEmployees = async (params: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'exclusion/incumbentemployees',
      method: 'GET',
      params,
    });
    return response.data;
  }
  catch (error: any) {
    console.error('Error fetching incumbent employees:', error);
    return error?.response?.data ?? HttpStatusCodeResponse.InternalServerError;
  }
};

export const getAdhocEmployees = async (params: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'exclusion/adhoc',
      method: 'GET',
      params,
    });
    return response.data;
  }
  catch (error: any) {
    console.error('Error fetching ad-hoc employees:', error);
    return error?.response?.data ?? HttpStatusCodeResponse.InternalServerError;
  }
};

export const postAdhocEmployees = async (data: any): Promise<any> => {
  try {
    const response = await kf1AuthenticatedHttpClient({
      baseURL: apiURL,
      url: 'exclusion/adhoc',
      method: 'POST',
      data,
    });
    return response.data;
  }
  catch (error: any) {
    console.error('Error posting ad-hoc employees:', error);
    return error?.response?.data ?? HttpStatusCodeResponse.InternalServerError;
  }
};